import { useEffect, useState } from 'react';
import { Icon, type IconName } from '../components/Icon';
import { Loading, ErrorView } from '../components/ViewState';
import { api } from '../data/api';
import { useClients } from '../data/store';
import { euro, shortDate } from '../data/helpers';
import type { Cadence, Expense, ExpenseCategory, ExpenseInput } from '../data/types';

const CADENCE_LABEL: Record<Cadence, string> = {
  monthly: 'Mensile',
  yearly: 'Annuale',
  oneoff: 'Una tantum',
};

const EXPENSE_CAT: Record<ExpenseCategory, string> = {
  infra: 'Infrastruttura',
  domini: 'Domini',
  strumenti: 'Strumenti',
  amministrazione: 'Amministrazione',
  marketing: 'Marketing',
  altro: 'Altro',
};

const CAT_ICON: Record<ExpenseCategory, IconName> = {
  infra: 'globe',
  domini: 'tag',
  strumenti: 'spark',
  amministrazione: 'list',
  marketing: 'chart',
  altro: 'tag',
};

/** Monthly-equivalent cost (one-offs don't recur). */
const monthlyCost = (e: Expense): number =>
  e.cadence === 'monthly' ? e.amount : e.cadence === 'yearly' ? e.amount / 12 : 0;

export function ExpensesView() {
  const clients = useClients();
  const mrr = clients.reduce((s, c) => s + c.mrr, 0);

  const [items, setItems] = useState<Expense[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Expense | 'new' | null>(null);

  const load = async () => {
    try {
      setItems(await api.expenses());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
  }, []);

  if (err) return <ErrorView detail={err} />;
  if (!items) return <Loading />;

  const expenses = items;
  const monthly = expenses.reduce((s, e) => s + monthlyCost(e), 0);
  const margin = mrr - monthly;
  const marginPct = mrr > 0 ? Math.round((margin / mrr) * 100) : null;

  const cats = (Object.keys(EXPENSE_CAT) as ExpenseCategory[])
    .map((cat) => ({ cat, total: expenses.filter((e) => e.category === cat).reduce((s, e) => s + monthlyCost(e), 0) }))
    .filter((c) => c.total > 0)
    .sort((a, b) => b.total - a.total);
  const catMax = Math.max(...cats.map((c) => c.total), 1);

  return (
    <div className="view">
      <div className="view-pad rs-stack">
        <div className="rs-actionbar">
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} />
            <span className="btn-label">Nuova spesa</span>
          </button>
        </div>

        <div className="kpi-row">
          <Kpi icon="chart" val={euro(Math.round(monthly))} label="Spese mensili" />
          <Kpi icon="building" val={euro(Math.round(monthly * 12))} label="Spese annuali" />
          <Kpi icon="spark" val={euro(Math.round(margin))} label="Margine mensile (lordo)" delta={marginPct != null ? `${marginPct}% sui ricavi` : undefined} />
          <Kpi icon="tag" val={String(expenses.length)} label="Voci di spesa" />
        </div>

        <div className="rs-2col">
          <div className="rs-card">
            <div className="rs-card-head">
              <span className="rs-card-title">Spese per categoria</span>
              <span className="rs-muted" style={{ fontSize: 12 }}>al mese</span>
            </div>
            {cats.length ? (
              <div className="rs-stack" style={{ gap: 13 }}>
                {cats.map((c) => (
                  <div key={c.cat} className="rs-hbar-row">
                    <span className="rs-hbar-label">{EXPENSE_CAT[c.cat]}</span>
                    <span className="rs-hbar-track">
                      <span className="rs-hbar-fill" style={{ width: `${(c.total / catMax) * 100}%` }} />
                    </span>
                    <span className="rs-hbar-val">{euro(Math.round(c.total))}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rs-muted" style={{ fontSize: 13 }}>Nessuna spesa ricorrente.</div>
            )}
          </div>

          <div className="rs-card">
            <div className="rs-card-head">
              <span className="rs-card-title">Flusso mensile</span>
            </div>
            <div className="rs-dl">
              <Row k="Ricavi (MRR)" v={euro(mrr)} />
              <Row k="− Spese ricorrenti" v={`− ${euro(Math.round(monthly))}`} />
              <Row k="= Margine lordo" v={euro(Math.round(margin))} strong />
              <Row k="− Tasse (stima)" v="da impostare" dim />
            </div>
            <div className="rs-note" style={{ marginTop: 12 }}>
              Le provvigioni partner andranno calcolate sul <b>netto</b> (dopo tasse e spese).
            </div>
          </div>
        </div>

        <div className="rs-table exp">
          <div className="rs-thead">
            <span>Voce</span>
            <span className="rs-hide-sm">Categoria</span>
            <span className="num">Importo</span>
            <span className="rs-hide-sm">Frequenza</span>
            <span className="num rs-hide-sm">Al mese</span>
          </div>
          {expenses.length ? (
            expenses.map((e) => {
              const meta = [e.vendor, e.note, e.date ? shortDate(e.date) : null].filter(Boolean).join(' · ');
              return (
                <div className="rs-row exp-row" key={e.id} onClick={() => setEditing(e)} role="button" tabIndex={0}
                  onKeyDown={(ev) => { if (ev.key === 'Enter') setEditing(e); }}>
                  <span className="rs-cli">
                    <span className="exp-ico">
                      <Icon name={CAT_ICON[e.category]} size={15} />
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span className="rs-cli-name">{e.name}</span>
                      <span className="rs-cli-meta">{meta || '—'}</span>
                    </span>
                  </span>
                  <span className="rs-cell rs-hide-sm">
                    <span className="rs-chip cat">{EXPENSE_CAT[e.category]}</span>
                  </span>
                  <span className="rs-cell num">{euro(e.amount)}</span>
                  <span className="rs-cell rs-hide-sm rs-dim">{CADENCE_LABEL[e.cadence]}</span>
                  <span className="rs-cell num rs-hide-sm">
                    {e.cadence === 'oneoff' ? <span className="rs-dim">—</span> : euro(Math.round(monthlyCost(e)))}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="rs-muted" style={{ padding: '22px 16px', fontSize: 13 }}>
              Nessuna spesa ancora. Aggiungi la prima con <b>Nuova spesa</b>.
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ExpenseModal
          expense={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

function ExpenseModal({ expense, onClose, onSaved }: { expense: Expense | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(expense?.name ?? '');
  const [vendor, setVendor] = useState(expense?.vendor ?? '');
  const [category, setCategory] = useState<ExpenseCategory>(expense?.category ?? 'infra');
  const [amount, setAmount] = useState<string>(expense ? String(expense.amount) : '');
  const [cadence, setCadence] = useState<Cadence>(expense?.cadence ?? 'monthly');
  const [date, setDate] = useState(expense?.date ?? '');
  const [note, setNote] = useState(expense?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountNum = Number(amount);
  const valid = name.trim().length > 0 && amount.trim() !== '' && Number.isFinite(amountNum) && amountNum >= 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    const input: ExpenseInput = {
      name: name.trim(),
      vendor: vendor.trim() || null,
      category,
      amount: amountNum,
      cadence,
      date: date || null,
      note: note.trim() || null,
    };
    try {
      if (expense) await api.patchExpense(expense.id, input);
      else await api.createExpense(input);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!expense || busy) return;
    // eslint-disable-next-line no-alert
    if (!confirm(`Eliminare "${expense.name}"?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteExpense(expense.id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="mscrim open" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={expense ? 'Modifica spesa' : 'Nuova spesa'} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{expense ? 'Modifica spesa' : 'Nuova spesa'}</h3>
          <button type="button" className="dr-close" onClick={onClose} aria-label="Chiudi"><Icon name="x" size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Voce</label>
            <input className="input" value={name} placeholder="Server, commercialista…" autoFocus onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field-row">
            <div className="field">
              <label>Fornitore</label>
              <input className="input" value={vendor ?? ''} placeholder="DigitalOcean…" onChange={(e) => setVendor(e.target.value)} />
            </div>
            <div className="field">
              <label>Categoria</label>
              <select className="input" value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)}>
                {(Object.keys(EXPENSE_CAT) as ExpenseCategory[]).map((k) => <option key={k} value={k}>{EXPENSE_CAT[k]}</option>)}
              </select>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Importo (€)</label>
              <input className="input" type="number" min="0" step="0.01" value={amount} placeholder="4.80" onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="field">
              <label>Frequenza</label>
              <select className="input" value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)}>
                <option value="monthly">Mensile</option>
                <option value="yearly">Annuale</option>
                <option value="oneoff">Una tantum</option>
              </select>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Data</label>
              <input className="input" type="date" value={date ?? ''} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="field">
              <label>Note</label>
              <input className="input" value={note ?? ''} placeholder="opzionale" onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
          {error && <div className="rs-error">{error}</div>}
        </div>
        <div className="modal-foot">
          {expense && (
            <button type="button" className="btn btn-ghost text-danger" onClick={remove} disabled={busy}>
              Elimina
            </button>
          )}
          <div className="grow" />
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Annulla</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!valid || busy}>
            {busy ? 'Salvo…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, val, label, delta }: { icon: IconName; val: string; label: string; delta?: string }) {
  return (
    <div className="kpi">
      <div className="kpi-top">
        <span className="kpi-ico">
          <Icon name={icon} size={17} />
        </span>
        {delta && <span className="kpi-delta">{delta}</span>}
      </div>
      <span className="kpi-val">{val}</span>
      <span className="kpi-label">{label}</span>
    </div>
  );
}

function Row({ k, v, strong, dim }: { k: string; v: string; strong?: boolean; dim?: boolean }) {
  return (
    <div className="rs-dl-row">
      <span className="rs-dl-k" style={strong ? { color: 'var(--ink)', fontWeight: 600 } : undefined}>{k}</span>
      <span className="rs-dl-v" style={{ fontWeight: strong ? 700 : 500, color: dim ? 'var(--mute)' : undefined, fontFamily: strong ? 'var(--display)' : undefined }}>
        {v}
      </span>
    </div>
  );
}
